import { useContext, useEffect, useRef, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { GroupListWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { GroupsTable } from "../../../containers/tables/groupstable";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAllGroupsService } from "../../../utils/api/customerservice/grouplist/retrieveAllGroups";

export const GroupList = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);
	const dropdownRef = useRef<HTMLUListElement | null>(null);

	const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Customer Service");
	}, [setActiveNavItem]);

	const { data: verifiedGroups } = useQuery({
		queryKey: ["groups", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllGroupsService(TOKEN, paginationIndex);
			const groups = response.data;
			if (!groups || groups.length === 0) return [];
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			setIsFetching(false);
			return groups;
		},
		enabled: !!TOKEN,
	});

	const handleCreateGroupButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/customerservice/addgrouplist");
	};

	const handleEllipsisButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return setSelectedGroupId(id);
	};

	const handleActionItemClick = (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		id: string | null,
		action: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!id) return;
		switch (action) {
			case "Delete Group":
				// delete action
				break;
			case "Edit Group":
				navigate(`/customerlist/group/${id}/edit`);
				break;
			default:
				break;
		}
		return;
	};

	const handleDropDownClickOutside = (event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			return setSelectedGroupId(null);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	useEffect(() => {
		if (selectedGroupId !== null) {
			document.addEventListener("mousedown", handleDropDownClickOutside);
		} else {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		};
	}, [selectedGroupId]);

	return (
		<AppLayout pageId={"Group-List"} pageTitle={"Group List"}>
			<GroupListWrapper>
				<Box>
					<BaseButton
						variant="contained"
						bgcolor="var(--primary-color)"
						padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
						startIcon={<AddIcon />}
						sx={{
							width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							float: { miniTablet: "right" },
						}}
						onClick={handleCreateGroupButtonClick}
					>
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Create Group
						</Typography>
					</BaseButton>
				</Box>
				<Box component={"div"} className="group-list-table-box">
					<GroupsTable
						rows={verifiedGroups as any[]}
						dropdownRef={dropdownRef}
						selectedGroupId={selectedGroupId}
						handleActionItemClick={handleActionItemClick}
						handleEllipsisButtonClick={handleEllipsisButtonClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</GroupListWrapper>
		</AppLayout>
	);
};
